import type { CirclesEngineState } from '@shared/engine/circles/state';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * Top-centre HP bar with damage juice. Owns the per-frame animation
 * state for the bleed segment, bar shake, and eased HP value. Split
 * from {@link HudRenderer} so each module stays focused — the score
 * + combo + tallies are independent of HP state.
 *
 * Three layered visuals:
 *
 *   1. **Bleed segment** — the just-lost slice of the bar is painted
 *      red over the green/amber base for {@link hpFlashT} seconds,
 *      so the player can see exactly how much HP a hit cost without
 *      doing mental subtraction on the numeric readout.
 *   2. **Bar shake** — the entire bar wobbles horizontally for a
 *      few hundred ms after damage, decaying with `hpShakeT`. Tied
 *      to the same trigger as the bleed so the player's eye snaps
 *      to the bar.
 *   3. **Numeric pulse** — the `hp / max` text pops bright red and
 *      scales up briefly on damage, then settles back to white.
 *
 * Eased `displayedHp` paints the foreground so the bar drains
 * smoothly toward the engine's authoritative value rather than
 * snap-cutting. Combined with the bleed segment, the visual effect
 * is "engine HP drops → red overlay marks the loss → green bar
 * shrinks to meet it". Reads instantly.
 */
export class HpBarRenderer {
  /**
   * Tracks the engine HP we rendered last frame; a drop triggers a
   * damage pulse + brief shake on the bar itself so the player sees
   * their HP take a hit rather than just noticing the static bar got
   * shorter on the next glance.
   */
  private lastShownHp = -1;
  /**
   * Lerps toward the live engine value so the bar "drains" smoothly
   * instead of snapping — pairs with the score count-up for a
   * consistent HUD-easing vocabulary.
   */
  private displayedHp = -1;
  /** Decays a red overlay drawn over the just-lost segment. */
  private hpFlashT = 0;
  /** Decays the horizontal shake applied to the entire bar. */
  private hpShakeT = 0;
  private hpFlashFromRatio = 0;
  private hpFlashToRatio = 0;
  private reducedMotion: boolean;
  private quality: RenderQuality;

  constructor(reducedMotion: boolean, quality: RenderQuality) {
    this.reducedMotion = reducedMotion;
    this.quality = quality;
  }

  /**
   * Re-init HP tracking so the next frame snaps `displayedHp` to the
   * engine's freshly-reset value (typically maxHp at loop start)
   * instead of treating the jump from end-of-loop HP to full HP as a
   * damage event — which would otherwise paint a giant bleed segment
   * and shake the bar on every restart.
   */
  clearTracking(): void {
    this.lastShownHp = -1;
    this.displayedHp = -1;
  }

  /**
   * Render-harness hook: pretend the player's HP was `priorHp` on the
   * previous frame. The next call to {@link draw} will then see
   * `state.hp < lastShownHp`, trigger the bleed-segment, bar shake,
   * and numeric-pulse animations that normally fire on a real damage
   * event. Required because the harness state is rebuilt from scratch
   * each capture and never sees a transition from high HP to lower HP
   * on its own.
   *
   * NOT for use in production — the live session relies on
   * frame-to-frame HP comparison and would skip a real damage event
   * if a caller pre-set this value. Naming makes the test-only intent
   * explicit.
   */
  testOnlyPrimeHpForHarness(priorHp: number): void {
    this.lastShownHp = priorHp;
    this.displayedHp = priorHp;
  }

  /**
   * Detect HP transitions and decay running animations. Called once
   * per frame from the HUD renderer before {@link draw}.
   */
  tick(state: CirclesEngineState, animDt: number): void {
    if (this.lastShownHp < 0) {
      this.lastShownHp = state.hp;
      this.displayedHp = state.hp;
    } else if (state.hp < this.lastShownHp - 0.01 && state.maxHp > 0) {
      this.hpFlashT = 1;
      this.hpShakeT = 1;
      this.hpFlashFromRatio = Math.max(0, Math.min(1, this.lastShownHp / state.maxHp));
      this.hpFlashToRatio = Math.max(0, Math.min(1, state.hp / state.maxHp));
    }
    this.lastShownHp = state.hp;
    // Ease the displayed HP toward the engine value. Same exponential
    // approach the score uses (~15/s → 95% in ~200ms) so the drain
    // animation feels paced rather than snappy.
    const hpLerp = animDt > 0 ? 1 - Math.exp(-15 * animDt) : 0;
    this.displayedHp = this.displayedHp + (state.hp - this.displayedHp) * hpLerp;

    if (this.reducedMotion) {
      this.hpFlashT = 0;
      this.hpShakeT = 0;
      return;
    }
    // HP flash decays a touch slower than score (3/s vs 6/s) so the
    // bleed segment stays visible long enough to read on rapid taps;
    // shake decays faster (10/s) so the wobble snaps off cleanly
    // without dragging.
    this.hpFlashT = Math.max(0, this.hpFlashT - animDt * 3);
    this.hpShakeT = Math.max(0, this.hpShakeT - animDt * 10);
  }

  draw(ctx: CanvasRenderingContext2D, state: CirclesEngineState, w: number): void {
    const max = state.maxHp;
    if (max <= 0) {
      return;
    }
    const ratio = Math.max(0, Math.min(1, this.displayedHp / max));
    const barW = 240;
    const barH = 16;
    const x = (w - barW) / 2;
    const y = 22;

    // Bar shake — horizontal jitter sourced from a frequency-locked
    // sine so successive frames don't look like random noise. Decays
    // with hpShakeT so the wobble snaps off cleanly. Skipped under
    // reduced-motion.
    const shakeX =
      this.hpShakeT > 0 && !this.reducedMotion
        ? Math.sin(performance.now() * 0.05) * 5 * this.hpShakeT
        : 0;

    ctx.save();
    ctx.translate(shakeX, 0);

    // Frame: track background + crisp 1px border. Drawn first so the
    // foreground / bleed / shadow stack reads on top.
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.fillRect(x, y, barW, barH);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, barW - 1, barH - 1);

    // Bleed segment — paint the difference between the previous-tick
    // HP and the current eased HP in red, so the player sees the
    // chunk they just lost. The segment fades over hpFlashT seconds.
    // Drawn between the background and the live fill so the live
    // green/amber/red bar slides in on top as the easing catches up.
    if (this.hpFlashT > 0 && this.hpFlashFromRatio > this.hpFlashToRatio) {
      const fromX = x + 1 + (barW - 2) * this.hpFlashToRatio;
      const segW = (barW - 2) * (this.hpFlashFromRatio - this.hpFlashToRatio);
      ctx.fillStyle = `rgba(255, 59, 92, ${0.85 * this.hpFlashT})`;
      ctx.fillRect(fromX, y + 1, segW, barH - 2);
    }

    const colour = ratio > 0.5 ? '#3bff9d' : ratio > 0.25 ? '#ffae3b' : '#ff3b5c';
    ctx.fillStyle = colour;
    if (this.quality.chromeShadows) {
      ctx.shadowColor = colour;
      // Glow brightens with the flash so a fresh hit briefly halos
      // the entire bar in addition to the bleed segment.
      ctx.shadowBlur = 12 + 16 * this.hpFlashT;
    }
    ctx.fillRect(x + 1, y + 1, (barW - 2) * ratio, barH - 2);
    ctx.shadowBlur = 0;

    // Numeric readout. Pops bright red + scales up on damage so the
    // number is the second thing the eye lands on after the bar.
    const txtScale = 1 + this.hpFlashT * 0.35;
    const txtColour = this.hpFlashT > 0.5 ? '#ff8694' : '#fff';
    ctx.save();
    ctx.translate(w / 2, y + barH + 14);
    ctx.scale(txtScale, txtScale);
    ctx.font = 'bold 12px monospace';
    ctx.fillStyle = txtColour;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(`${Math.round(state.hp)} / ${max}`, 0, 0);
    ctx.restore();

    ctx.restore();
  }
}
